<template>
  <button
    v-show="!wechatServiceOpen"
    type="button"
    class="wechat-kf-float"
    aria-label="在线咨询"
    @click="openWeChatService"
  >
    在线<br />咨询
  </button>

  <dialog
    ref="dialogRef"
    class="wechat-kf-dialog"
    aria-labelledby="wechat-kf-title"
    aria-describedby="wechat-kf-desc"
    @cancel.prevent="closeWeChatService"
    @click="onDialogClick"
    @close="syncClosedState"
  >
    <section class="wechat-kf-card" @click.stop>
      <button type="button" class="wechat-kf-close" aria-label="关闭客服弹窗" @click="closeWeChatService">
        <svg width="19" height="19" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7" aria-hidden="true">
          <path d="m6 6 12 12M18 6 6 18"></path>
        </svg>
      </button>
      <div class="wechat-kf-icon" aria-hidden="true">
        <svg width="39" height="39" viewBox="0 0 48 48" fill="none">
          <path d="M29.6 25.2c0 7-6.3 12.5-14.1 12.5-1.7 0-3.3-.3-4.8-.8L5 40l1.5-5a11.8 11.8 0 0 1-5.1-9.8c0-7 6.3-12.6 14.1-12.6s14.1 5.6 14.1 12.6Z" fill="white" transform="translate(2 -6)"></path>
          <path d="M46 31.3c0 5.9-5.4 10.7-12 10.7-1.5 0-2.9-.2-4.2-.7l-4.5 2.4 1.2-4.2a10 10 0 0 1-4.5-8.2c0-5.9 5.4-10.7 12-10.7s12 4.8 12 10.7Z" fill="white" stroke="#50b674" stroke-width="2"></path>
          <circle cx="12" cy="16.5" r="1.6" fill="#50b674"></circle>
          <circle cx="22" cy="16.5" r="1.6" fill="#50b674"></circle>
          <circle cx="30" cy="29" r="1.4" fill="#50b674"></circle>
          <circle cx="38" cy="29" r="1.4" fill="#50b674"></circle>
        </svg>
      </div>
      <h1 id="wechat-kf-title">添加微信客服</h1>
      <p class="wechat-kf-subtitle" id="wechat-kf-desc">关于你的疑问，我们用心解答</p>
      <div class="wechat-kf-qr-shell">
        <img
          v-show="!qrFailed"
          class="wechat-kf-qr"
          alt="客服微信二维码"
          width="313"
          height="316"
          :src="qrSrc"
          @error="qrFailed = true"
        />
        <div v-if="qrFailed" class="wechat-kf-error">二维码加载失败，请刷新重试</div>
      </div>
      <p class="wechat-kf-scan">
        <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" aria-hidden="true">
          <path d="M8 3H3v5m13-5h5v5M3 16v5h5m13-5v5h-5M4 12h16"></path>
        </svg>
        打开微信，扫一扫添加
      </p>
    </section>
  </dialog>
</template>

<script setup>
import { nextTick, onBeforeUnmount, ref, watch } from "vue";
import qrSrc from "@/assets/image/wechat-kf-qr.jpg";
import { closeWeChatService, openWeChatService, wechatServiceOpen } from "@/utlis/wechatService";

const ANIMATION_MS = 220;
const dialogRef = ref(null);
const qrFailed = ref(false);
let closeTimer = 0;
let openFrame = 0;

function syncClosedState() {
  wechatServiceOpen.value = false;
}

function onDialogClick(event) {
  if (event.target === dialogRef.value) {
    closeWeChatService();
  }
}

function clearMotionTimers() {
  window.clearTimeout(closeTimer);
  if (openFrame) {
    window.cancelAnimationFrame(openFrame);
    openFrame = 0;
  }
}

function paintClosedState(dialog) {
  dialog.classList.remove("is-open");
}

function paintOpenState(dialog) {
  openFrame = window.requestAnimationFrame(() => {
    openFrame = window.requestAnimationFrame(() => {
      openFrame = 0;
      if (wechatServiceOpen.value) {
        dialog.classList.add("is-open");
      }
    });
  });
}

watch(
  wechatServiceOpen,
  async (open) => {
    await nextTick();
    const dialog = dialogRef.value;
    if (!dialog) return;
    clearMotionTimers();
    if (open) {
      if (!dialog.open) dialog.showModal();
      paintOpenState(dialog);
      return;
    }
    if (!dialog.open) {
      paintClosedState(dialog);
      return;
    }
    paintClosedState(dialog);
    closeTimer = window.setTimeout(() => {
      if (!wechatServiceOpen.value && dialog.open) {
        dialog.close();
      }
    }, ANIMATION_MS);
  },
);

onBeforeUnmount(clearMotionTimers);
</script>

<style lang="scss">
.wechat-kf-float {
  position: fixed;
  right: 0;
  top: 50%;
  z-index: 999;
  width: 40px;
  padding: 10px 0;
  transform: translateY(-50%);
  border: 0;
  background: #f07800;
  color: #fff;
  font-size: 11px;
  font-weight: 600;
  line-height: 1.4;
  text-align: center;
  cursor: pointer;
  box-shadow: 0 4px 16px rgba(240, 120, 0, 0.28);

  &:hover {
    background: #d46000;
  }
}

.wechat-kf-dialog {
  width: min(410px, calc(100vw - 32px));
  max-height: calc(100dvh - 32px);
  padding: 0;
  border: 1px solid #eaf0ec;
  border-radius: 26px;
  background: #fff;
  color: #26382e;
  overflow: auto;
  box-shadow: 0 16px 60px #203f3010;
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", "PingFang SC", "Microsoft YaHei", sans-serif;
  opacity: 0;
  transform: scale(0.92) translateY(12px);
  transition: opacity 0.22s ease, transform 0.22s ease;

  &.is-open {
    opacity: 1;
    transform: scale(1) translateY(0);
  }

  &::backdrop {
    background: rgba(0, 0, 0, 0.55);
    opacity: 0;
    transition: opacity 0.22s ease;
  }

  &.is-open::backdrop {
    opacity: 1;
  }
}

.wechat-kf-card {
  position: relative;
  padding: 38px 32px 30px;
  text-align: center;
  background: radial-gradient(ellipse at 50% 0%, #effaf3, transparent 58%);
}

.wechat-kf-close {
  position: absolute;
  top: 16px;
  right: 16px;
  width: 34px;
  height: 34px;
  display: grid;
  place-items: center;
  padding: 0;
  border: 0;
  border-radius: 50%;
  background: transparent;
  color: #8a978f;
  cursor: pointer;

  &:hover {
    background: #eaf2ed;
    color: #385542;
  }

  &:focus-visible {
    outline: 2px solid #50b674;
    outline-offset: 2px;
  }
}

.wechat-kf-icon {
  width: 62px;
  height: 62px;
  margin: 0 auto 20px;
  border-radius: 21px;
  background: #50b674;
  box-shadow: 0 10px 26px #50b6741c;
  display: grid;
  place-items: center;
}

.wechat-kf-card h1 {
  margin: 0 0 10px;
  font-size: 25px;
  font-weight: 700;
  color: #26382e;
}

.wechat-kf-subtitle {
  margin: 0;
  font-size: 14px;
  color: #8a978f;
  line-height: 1.7;
}

.wechat-kf-qr-shell {
  width: 228px;
  max-width: 100%;
  position: relative;
  margin: 27px auto 20px;
  padding: 13px;
  border: 1px solid #e2ebe5;
  border-radius: 16px;
  background: #fff;
  box-shadow: 0 8px 26px #234d350b;

  &::before,
  &::after {
    content: "";
    position: absolute;
    width: 24px;
    height: 24px;
    border-style: solid;
    border-color: #50b674;
    pointer-events: none;
  }

  &::before {
    left: -1px;
    top: -1px;
    border-width: 2px 0 0 2px;
    border-radius: 16px 0 0 0;
  }

  &::after {
    right: -1px;
    bottom: -1px;
    border-width: 0 2px 2px 0;
    border-radius: 0 0 16px 0;
  }
}

.wechat-kf-qr {
  display: block;
  width: 100%;
  height: auto;
}

.wechat-kf-scan {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  margin: 0;
  font-size: 14px;
  font-weight: 600;
  color: #526b5b;

  svg {
    color: #50b674;
    flex-shrink: 0;
  }
}

.wechat-kf-error {
  padding: 30px 8px;
  font-size: 13px;
  line-height: 1.8;
  color: #7d8e83;
}

@media (max-width: 480px) {
  .wechat-kf-card {
    padding: 34px 24px 28px;
  }
}
</style>
